import {
  Component,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import { CircleMarker, Polygon, Polyline } from 'leaflet';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import {
  SPAN_POPUP_CLOSED,
  SPAN_POPUP_CLOSED_BY_X,
} from '../../../shared/posthug-analytics/analytics-event-types';
import { AnalyticsService } from '../../../shared/posthug-analytics/analytics.service';
import { RolloutProgressReportEntities } from '../../../shared/self-inspection-reports/service/entities';
import { InspectionReportInitService } from '../../../shared/self-inspection-reports/service/initial-values/inspection-report-init.service';
import { RolloutProgressReportsService } from '../../../shared/self-inspection-reports/service/reports/rollout-progress-reports.service';
import { getIfsFromUrl } from './popup-pdf.utils';
import {
  DistributionPointProperties,
  PointType,
  PopupTypes,
  SelectedMapItem,
} from '../../span-progress-entities';
import { MapInteractionService } from '../map-interaction/map-interaction.service';

@Component({
  selector: 'app-popup-content',
  template: `
    <ng-container [ngSwitch]="typeId">
      <a
        class="leaflet-popup-close-button cursor-pointer"
        (click)="closeModal($event)"
        *ngIf="shouldShowCloseButton"
      >
        <gc-icon
          class="close-icon"
          [iconName]="'minimize'"
          [color]="'invert'"
        />
      </a>
      <ng-container *ngIf="properties.netadminId">
        <div class="net-admin-id-text-container">
          <ipo-text [weight]="'bold'">NetAdmin ID:</ipo-text>
          <ipo-text>{{ properties.netadminId }}</ipo-text>
        </div>
        <div class="divider"></div>
      </ng-container>
      <app-span-popup-content
        *ngSwitchCase="PopupTypes.Span"
        [properties]="properties"
        class="popup-content"
        (dismiss)="closePopup()"
      ></app-span-popup-content>
      <app-info-point-popup-content
        *ngSwitchCase="PopupTypes.Info"
        [properties]="properties"
        (dismiss)="closePopup()"
      ></app-info-point-popup-content>
      <app-reportable-point-popup-content
        *ngSwitchCase="PopupTypes.Site"
        [properties]="properties"
        [status]="(status | async) ?? ''"
        [markerType]="getType()!"
        (dismiss)="closePopup()"
      ></app-reportable-point-popup-content>
      <app-reportable-point-popup-content
        *ngSwitchCase="PopupTypes.Hub"
        [properties]="properties"
        [status]="(status | async) ?? ''"
        [markerType]="getType()!"
        (dismiss)="closePopup()"
      ></app-reportable-point-popup-content>
      <app-distribution-point-popup-content
        *ngSwitchCase="PopupTypes.DistPoint"
        [properties]="properties"
        [status]="(status | async) ?? ''"
        (dismiss)="closePopup()"
      ></app-distribution-point-popup-content>
      <app-households-point-popup-content
        *ngSwitchCase="PopupTypes.Households"
        [properties]="properties"
        [clickedObject]="clickedObject"
        (dismiss)="closePopup()"
      >
      </app-households-point-popup-content>
      <div *ngSwitchCase="PopupTypes.LandAgreement">
        {{ properties.name }}
      </div>
      <app-permit-area-popup-content
        *ngSwitchCase="PopupTypes.PermitArea"
        [properties]="properties"
      >
      </app-permit-area-popup-content>
      <app-ductpoint-popup-content
        *ngSwitchCase="PopupTypes.Ductpoint"
        [properties]="properties"
        [initialExpandedPointId]="expandedPointId"
        (dismiss)="closePopup()"
      ></app-ductpoint-popup-content>
    </ng-container>
  `,
  styleUrls: ['./popup-content.component.scss'],
  standalone: false,
})
export class PopupContentComponent implements OnInit, OnDestroy, OnChanges {
  private readonly mapInteractionService = inject(MapInteractionService);
  private readonly router = inject(Router);
  private readonly reportService = inject(RolloutProgressReportsService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly inspectionReportInitService = inject(
    InspectionReportInitService,
  );

  @Input() properties: any;
  @Input() typeId: PopupTypes;
  @Input() clickedObject: CircleMarker | Polyline | Polygon;
  @Input() shouldShowCloseButton = true;
  @Input() expandedPointId?: string;

  private readonly cancelSub$ = new Subject<void>();

  PopupTypes = PopupTypes;

  status = new BehaviorSubject<
    RolloutProgressReportEntities.v3.ReportStatus | undefined
  >(undefined);

  ngOnInit(): void {
    this.setReportUrl();
  }

  ngOnDestroy(): void {
    this.cancelSub$.next();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.properties) {
      this.getStatus();
    }
  }

  setReportUrl = () => {
    const type = this.getType();

    if (type) {
      this.inspectionReportInitService.setReportUrlInitValue(
        RolloutProgressReportEntities.v3.inspectionReportUrlTypes.spanProgress
          .report,
      );
    }
  };

  getStatus() {
    this.status.next(undefined);
    const ifs = this.getIfs();
    const type = this.getType();

    if (type) {
      this.reportService
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        .getReport$(ifs, type, this.properties.id)
        .pipe(takeUntil(this.cancelSub$))
        .subscribe((res) => {
          if (res.data?.status) {
            this.status.next(res.data.status);
          }
        });
    }
  }

  getType(): RolloutProgressReportEntities.v3.MarkerTypes | undefined {
    if (this.typeId === PopupTypes.Site) {
      return RolloutProgressReportEntities.v3.MarkerTypes.SITE;
    }
    if (this.typeId === PopupTypes.Hub) {
      return RolloutProgressReportEntities.v3.MarkerTypes.HUB;
    }
    if (this.typeId === PopupTypes.DistPoint) {
      const markerTypeId = (this.properties as DistributionPointProperties)
        ?.markerTypeId as PointType | undefined;

      if (markerTypeId === PointType.Cabinet) {
        return RolloutProgressReportEntities.v3.MarkerTypes.CABINET;
      }
      if (
        markerTypeId === PointType.CableWell ||
        markerTypeId === PointType.SlingWell
      ) {
        return RolloutProgressReportEntities.v3.MarkerTypes.MANHOLE;
      }
    }
  }

  getIfs(): string {
    return getIfsFromUrl(this.router.url);
  }

  /** A fix for the leaflet popup close button bug in Leaflet version 1.8.0 */
  closeModal(event: MouseEvent) {
    event.preventDefault();
    this.clickedObject.closePopup();
    this.clickedObject.unbindPopup();
    if (this.typeId === this.PopupTypes.Span) {
      this.analyticsService.logEvent(SPAN_POPUP_CLOSED_BY_X, {});
    }
  }

  closePopup() {
    this.mapInteractionService.setSelectedResultControl$({} as SelectedMapItem);
    this.mapInteractionService.onSelectSearchedItem({} as CircleMarker);
    if (this.typeId === this.PopupTypes.Span) {
      this.analyticsService.logEvent(SPAN_POPUP_CLOSED, {});
    }
  }
}
